import { useMemo, useState } from 'react';
import { ChartFrame, Axes, ChartTooltip, SERIES_COLORS } from './ChartFrame';
import { scaleLinear, niceTicks } from './scales';
import { EmptyState } from '../ui/AsyncBoundary';

/**
 * Columns for magnitude over a small number of bands — daily distance, trips
 * per day.
 *
 * One series, one colour. Colouring each column darker-where-bigger would
 * double-encode length as hue and spend the only free channel on information
 * the chart already shows.
 *
 * Columns cap at 24px and never fill their band; the leftover is air. The cap
 * is rounded 4px and square at the baseline, so the bar reads as growing from
 * the axis rather than floating.
 */
export function ColumnChart({
  data,                        // [{ label, value, key? }]
  height = 200,
  formatValue = (v) => String(v),
  color = SERIES_COLORS[0],
  ariaLabel,
  emptyMessage = 'No activity in this range',
  highlightLast = false,
}) {
  const [hover, setHover] = useState(null);

  const model = useMemo(() => {
    if (!data?.length) return null;
    const max = Math.max(...data.map((d) => Number(d.value) || 0));
    const { ticks, domain } = niceTicks(0, max || 1, 4);
    return { yTicks: ticks, yDomain: domain };
  }, [data]);

  if (!model) return <EmptyState icon="chart" title={emptyMessage} />;

  return (
    <div className="relative">
      <ChartFrame
        height={height}
        ariaLabel={ariaLabel}
        onLeave={() => setHover(null)}
        onHover={({ x, plotWidth, offsetLeft }) => {
          const band = plotWidth / data.length;
          const index = Math.floor(x / band);
          if (index < 0 || index >= data.length) return setHover(null);
          setHover({
            index,
            pageX: band * index + band / 2 + offsetLeft,
            containerWidth: plotWidth + offsetLeft,
          });
        }}
      >
        {({ plotWidth, plotHeight }) => {
          const y = scaleLinear(model.yDomain, [plotHeight, 0]);
          const band = plotWidth / data.length;
          const barWidth = Math.min(24, band * 0.62);

          // Label every band when they fit, otherwise first / middle / last —
          // a label that will not fit is not drawn small, it is not drawn.
          const labelEvery = Math.ceil((data.length * 46) / Math.max(1, plotWidth));

          return (
            <>
              <Axes
                plotWidth={plotWidth}
                plotHeight={plotHeight}
                yTicks={model.yTicks}
                y={y}
                formatY={formatValue}
                xBandLabels={data
                  .map((d, index) => ({ label: d.label, x: band * index + band / 2, index }))
                  .filter((b) => b.index % labelEvery === 0)}
              />

              {data.map((d, index) => {
                const value = Number(d.value) || 0;
                const top = y(value);
                const barHeight = Math.max(value > 0 ? 2 : 0, plotHeight - top);
                const isHovered = hover?.index === index;
                const isLast = highlightLast && index === data.length - 1;

                return (
                  <g key={d.key ?? d.label}>
                    {/* full-height hit target, so hovering does not require
                        landing on a 3px column */}
                    <rect
                      x={band * index}
                      y={0}
                      width={band}
                      height={plotHeight}
                      fill="transparent"
                    />
                    <path
                      d={roundedTopBar(
                        band * index + (band - barWidth) / 2,
                        top,
                        barWidth,
                        barHeight,
                        4
                      )}
                      fill={isLast ? 'var(--accent)' : color}
                      opacity={hover && !isHovered ? 0.55 : 1}
                      className="transition-opacity"
                    />
                  </g>
                );
              })}
            </>
          );
        }}
      </ChartFrame>

      {hover && (
        <ChartTooltip
          x={hover.pageX}
          y={8}
          containerWidth={hover.containerWidth}
          title={data[hover.index].label}
          rows={[
            {
              label: data[hover.index].tooltipLabel || 'Value',
              color: highlightLast && hover.index === data.length - 1 ? 'var(--accent)' : color,
              value: formatValue(data[hover.index].value),
            },
          ]}
        />
      )}
    </div>
  );
}

/** Rounded at the data end, square at the baseline. */
function roundedTopBar(x, y, width, height, radius) {
  if (height <= 0) return '';
  const r = Math.min(radius, width / 2, height);
  return [
    `M${x} ${y + height}`,
    `L${x} ${y + r}`,
    `Q${x} ${y} ${x + r} ${y}`,
    `L${x + width - r} ${y}`,
    `Q${x + width} ${y} ${x + width} ${y + r}`,
    `L${x + width} ${y + height}`,
    'Z',
  ].join('');
}
